import React, { useState, useEffect } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { AdminRevenueChart } from '../../components/admin/AdminRevenueChart';
import { OperatorLogo, OPERATOR_CONFIGS } from '../../components/common/BrandLogo';
import {
  TrendingUp,
  Coins,
  CheckCircle2,
  Clock,
  RefreshCw,
  Sparkles,
  ArrowUpRight,
  Package,
  Layers,
  ArrowRight,
  ShieldCheck,
  AlertTriangle,
  Calendar,
  CalendarDays,
  ChevronDown,
  Filter,
  Check,
  Users,
} from 'lucide-react';
import { AdminTabType } from '../../components/layout/AdminBottomNav';
import { DashboardTimeframe } from '../../types';

interface AdminDashboardPageProps {
  onNavigateTab?: (tab: AdminTabType) => void;
}

export const AdminDashboardPage: React.FC<AdminDashboardPageProps> = ({ onNavigateTab }) => {
  const {
    dashboardStats,
    dashboardDays,
    dashboardFilter,
    setDashboardFilter,
    loadDashboardStats,
    pendingOrdersCount,
    pendingDepositsCount,
    pendingBuybacksCount,
    refresh,
  } = useAdmin();

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCustomPicker, setShowCustomPicker] = useState(dashboardFilter.days === 'custom');
  const [customStart, setCustomStart] = useState(
    dashboardStats?.dateRange?.startDate || new Date().toISOString().split('T')[0]
  );
  const [customEnd, setCustomEnd] = useState(
    dashboardStats?.dateRange?.endDate || new Date().toISOString().split('T')[0]
  );
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string>(() =>
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  );

  // Sync custom date state when backend dateRange arrives
  useEffect(() => {
    if (dashboardStats?.dateRange) {
      setCustomStart(dashboardStats.dateRange.startDate);
      setCustomEnd(dashboardStats.dateRange.endDate);
    }
  }, [dashboardStats?.dateRange]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([refresh(), loadDashboardStats(dashboardFilter)]);
      setLastRefreshedAt(
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      );
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSelectPreset = (val: DashboardTimeframe) => {
    if (val === 'custom') {
      setShowCustomPicker(true);
    } else {
      setShowCustomPicker(false);
      setDashboardFilter({ days: val });
    }
  };

  const handleApplyCustomFilter = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!customStart || !customEnd) return;
    if (customStart > customEnd) {
      alert('Start date cannot be after end date');
      return;
    }
    setDashboardFilter({
      days: 'custom',
      startDate: customStart,
      endDate: customEnd,
    });
  };

  // Quick helper for shortcut buttons inside custom picker
  const setQuickRange = (daysBack: number) => {
    const end = new Date();
    const start = new Date(end.getTime() - (daysBack - 1) * 24 * 60 * 60 * 1000);
    const sStr = start.toISOString().split('T')[0];
    const eStr = end.toISOString().split('T')[0];
    setCustomStart(sStr);
    setCustomEnd(eStr);
    setDashboardFilter({
      days: 'custom',
      startDate: sStr,
      endDate: eStr,
    });
  };

  const presetOptions: Array<{ label: string; value: DashboardTimeframe }> = [
    { label: 'Today', value: 'today' },
    { label: '7D', value: 7 },
    { label: '30D', value: 30 },
    { label: 'This Month', value: 'month' },
    { label: 'All Time', value: 'all' },
  ];

  const summary = dashboardStats?.summary;
  const currentRangeLabel =
    dashboardStats?.dateRange?.label ||
    (dashboardDays === 'all'
      ? 'All Time'
      : dashboardDays === 'today'
      ? 'Today'
      : dashboardDays === 'month'
      ? 'This Month'
      : `Last ${dashboardDays} Days`);

  return (
    <div className="space-y-4 pb-24 animate-in fade-in duration-200">
      {/* ========================================================================= */}
      {/* FINANCIAL DASHBOARD HEADER CARD (COMPACT & LIVE SYNC)                     */}
      {/* ========================================================================= */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-surface-card via-surface-card/95 to-primary/10 border border-plum-border/90 shadow-sm p-3.5 sm:p-4">
        {/* Subtle Ambient Glow */}
        <div className="pointer-events-none absolute -top-12 -right-12 w-36 h-36 bg-primary/10 rounded-full blur-3xl" />
        <div className="pointer-events-none absolute -bottom-12 -left-12 w-32 h-32 bg-status-green/10 rounded-full blur-3xl" />

        <div className="relative z-10 space-y-2.5">
          {/* Header Row: Title, LIVE SYNC & Refresh Button */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-1.5 rounded-xl bg-primary/20 text-primary border border-primary/30 shadow-xs shrink-0">
                <TrendingUp className="w-4 h-4" />
              </div>
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <h1 className="text-base sm:text-lg font-bold text-plum-dark tracking-tight truncate">
                  Financial Dashboard
                </h1>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  LIVE SYNC
                </span>
              </div>
            </div>

            {/* Refresh Button */}
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="p-1.5 sm:px-2.5 sm:py-1 rounded-xl bg-surface-page/80 hover:bg-surface-page border border-plum-border hover:border-primary/50 text-plum-dark hover:text-primary transition disabled:opacity-50 flex items-center gap-1.5 shrink-0 touch-target"
                title={`Refresh dashboard (Updated ${lastRefreshedAt})`}
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-primary' : ''}`} />
                <span className="hidden sm:inline font-medium text-[11px]">Refresh</span>
              </button>
            </div>
          </div>

          {/* Timeframe Presets Bar */}
          <div className="flex items-center gap-1 overflow-x-auto pb-0.5 scrollbar-none bg-surface-page/90 p-1 rounded-xl border border-plum-border">
            {presetOptions.map((opt) => {
              const isActive = !showCustomPicker && dashboardDays === opt.value;
              return (
                <button
                  key={String(opt.value)}
                  type="button"
                  onClick={() => handleSelectPreset(opt.value)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition touch-target ${
                    isActive
                      ? 'bg-primary text-white shadow-xs font-semibold'
                      : 'text-plum-muted hover:text-plum-dark'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}

            {/* Custom Range Pill Button */}
            <button
              type="button"
              onClick={() => setShowCustomPicker(!showCustomPicker)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition touch-target ${
                showCustomPicker || dashboardDays === 'custom'
                  ? 'bg-primary text-white shadow-xs font-semibold'
                  : 'text-plum-muted hover:text-plum-dark'
              }`}
            >
              <CalendarDays className="w-3 h-3" />
              <span>Custom</span>
              <ChevronDown className={`w-3 h-3 transition-transform ${showCustomPicker ? 'rotate-180' : ''}`} />
            </button>
          </div>

          {/* Active Range & Quick Stats Summary Line */}
          <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px] text-plum-muted px-0.5">
            <div className="flex items-center gap-1.5 text-primary-light font-medium min-w-0">
              <Calendar className="w-3 h-3 text-primary shrink-0" />
              <span className="truncate">{currentRangeLabel}</span>
              <span className="text-plum-muted/60">•</span>
              <span className="text-[10px] text-plum-muted shrink-0">Updated {lastRefreshedAt}</span>
            </div>

            <div className="flex items-center gap-1.5 shrink-0 text-[11px]">
              <span>Orders: <strong className="text-plum-dark">{summary ? summary.completedOrdersCount : 0}</strong></span>
              <span className="text-plum-muted/60">•</span>
              <span>Margin: <strong className="text-emerald-400">{summary ? summary.profitMargin : 0}%</strong></span>
            </div>
          </div>

          {/* Expandable Custom Date Range Drawer */}
          {showCustomPicker && (
            <div className="p-3 rounded-xl bg-surface-page/90 border border-primary/30 space-y-2.5 animate-in fade-in slide-in-from-top-2 duration-150">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-plum-dark flex items-center gap-1.5">
                  <Filter className="w-3.5 h-3.5 text-primary" />
                  Select Custom Date Range
                </span>
                <div className="flex items-center gap-1">
                  {[7, 14, 30, 90].map((d) => (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setQuickRange(d)}
                      className="text-[10px] px-2 py-0.5 rounded-md bg-surface-card border border-plum-border hover:border-primary/40 text-plum-muted hover:text-plum-dark transition"
                    >
                      {d}D
                    </button>
                  ))}
                </div>
              </div>

              {/* Start & End Date Inputs */}
              <form onSubmit={handleApplyCustomFilter} className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div>
                  <label className="block text-[10px] text-plum-muted mb-0.5 font-medium">
                    Start Date (From)
                  </label>
                  <input
                    type="date"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                    className="w-full bg-surface-card border border-plum-border rounded-lg px-2.5 py-1.5 text-xs text-plum-dark focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary"
                    required
                  />
                </div>

                <div>
                  <label className="block text-[10px] text-plum-muted mb-0.5 font-medium">
                    End Date (To)
                  </label>
                  <input
                    type="date"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                    className="w-full bg-surface-card border border-plum-border rounded-lg px-2.5 py-1.5 text-xs text-plum-dark focus:outline-hidden focus:border-primary focus:ring-1 focus:ring-primary"
                    required
                  />
                </div>

                <div className="flex items-end">
                  <button
                    type="submit"
                    className="w-full py-1.5 px-3 rounded-lg bg-primary hover:bg-primary-dark text-white font-semibold transition flex items-center justify-center gap-1 text-xs shadow-xs touch-target"
                  >
                    <Check className="w-3.5 h-3.5" />
                    <span>Apply Filter</span>
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>

      {/* Actionable Work Queue Banner (if items are pending) */}
      {(pendingOrdersCount > 0 || pendingDepositsCount > 0 || pendingBuybacksCount > 0) && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-3.5 flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="flex items-center gap-2 text-amber-300">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              <strong>Action Required:</strong> {pendingOrdersCount} orders, {pendingDepositsCount} deposits, and {pendingBuybacksCount} airtime buybacks waiting.
            </span>
          </div>
          {onNavigateTab && (
            <div className="flex items-center gap-2">
              {pendingOrdersCount > 0 && (
                <button
                  type="button"
                  onClick={() => onNavigateTab('admin-orders')}
                  className="px-2.5 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 transition font-medium"
                >
                  Orders ({pendingOrdersCount})
                </button>
              )}
              {pendingDepositsCount > 0 && (
                <button
                  type="button"
                  onClick={() => onNavigateTab('admin-deposits')}
                  className="px-2.5 py-1 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 transition font-medium"
                >
                  Deposits ({pendingDepositsCount})
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Primary KPI Grid: Revenue & Profit */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Total Revenue Card */}
        <div className="relative overflow-hidden bg-gradient-to-br from-surface-card via-surface-card to-primary/10 border border-plum-border hover:border-primary/40 rounded-2xl p-4 transition shadow-xs">
          <div className="flex items-start justify-between">
            <span className="text-xs font-medium text-plum-muted uppercase tracking-wider">
              Total Revenue ({currentRangeLabel})
            </span>
            <span className="p-2 rounded-xl bg-primary/15 text-primary border border-primary/25">
              <Coins className="w-5 h-5" />
            </span>
          </div>

          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-extrabold text-plum-dark tracking-tight">
              {summary ? (summary.totalRevenue || 0).toLocaleString() : '0'}
            </span>
            <span className="text-xs font-bold text-primary-light">Ks</span>
          </div>

          <div className="mt-3 pt-2.5 border-t border-plum-border/60 flex items-center justify-between text-xs text-plum-muted">
            <span>
              Today: <strong className="text-primary-light">+{summary ? (summary.todayRevenue || 0).toLocaleString() : '0'} Ks</strong>
            </span>
            <span>
              Lifetime: <strong>{summary ? (summary.lifetimeRevenue || 0).toLocaleString() : '0'} Ks</strong>
            </span>
          </div>
        </div>

        {/* Total Net Profit Card */}
        <div className="relative overflow-hidden bg-gradient-to-br from-surface-card via-surface-card to-status-green/10 border border-status-green/30 hover:border-status-green/50 rounded-2xl p-4 transition shadow-xs">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-plum-muted uppercase tracking-wider">
                Net Profit
              </span>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-status-green/20 text-emerald-400 border border-status-green/40">
                {summary ? summary.profitMargin : 0}% margin
              </span>
            </div>
            <span className="p-2 rounded-xl bg-status-green/15 text-status-green border border-status-green/25">
              <Sparkles className="w-5 h-5" />
            </span>
          </div>

          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-2xl sm:text-3xl font-extrabold text-emerald-400 tracking-tight">
              +{summary ? (summary.totalProfit || 0).toLocaleString() : '0'}
            </span>
            <span className="text-xs font-bold text-emerald-300">Ks</span>
          </div>

          <div className="mt-3 pt-2.5 border-t border-plum-border/60 flex items-center justify-between text-xs text-plum-muted">
            <span>
              Today: <strong className="text-emerald-400">+{summary ? (summary.todayProfit || 0).toLocaleString() : '0'} Ks</strong>
            </span>
            <span>
              Lifetime: <strong>+{summary ? (summary.lifetimeProfit || 0).toLocaleString() : '0'} Ks</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Secondary Metrics: Volume & Orders */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <div className="bg-surface-card border border-plum-border/80 rounded-xl p-3">
          <span className="text-[11px] text-plum-muted flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5 text-status-green" /> Delivered
          </span>
          <p className="text-lg font-bold text-plum-dark mt-1">
            {summary ? summary.completedOrdersCount : 0}
          </p>
          <span className="text-[10px] text-plum-muted">
            Avg: {summary ? summary.averageOrderValue.toLocaleString() : 0} Ks
          </span>
        </div>

        <div className="bg-surface-card border border-plum-border/80 rounded-xl p-3">
          <span className="text-[11px] text-plum-muted flex items-center gap-1">
            <Clock className="w-3.5 h-3.5 text-amber-400" /> In Queue
          </span>
          <p className="text-lg font-bold text-plum-dark mt-1">
            {summary ? summary.pendingOrdersCount : 0}
          </p>
          <span className="text-[10px] text-plum-muted">Awaiting fulfillment</span>
        </div>

        <div className="bg-surface-card border border-plum-border/80 rounded-xl p-3">
          <span className="text-[11px] text-plum-muted flex items-center gap-1">
            <ShieldCheck className="w-3.5 h-3.5 text-blue-400" /> Wallet Deposits
          </span>
          <p className="text-lg font-bold text-plum-dark mt-1">
            {summary ? (summary.totalDepositsCredited || 0).toLocaleString() : 0}
          </p>
          <span className="text-[10px] text-plum-muted">Ks credited</span>
        </div>

        <div className="bg-surface-card border border-plum-border/80 rounded-xl p-3">
          <span className="text-[11px] text-plum-muted flex items-center gap-1">
            <ArrowUpRight className="w-3.5 h-3.5 text-purple-400" /> Airtime Spread
          </span>
          <p className="text-lg font-bold text-plum-dark mt-1">
            +{summary ? (summary.buybackSpreadProfit || 0).toLocaleString() : 0}
          </p>
          <span className="text-[10px] text-plum-muted">Ks buyback gain</span>
        </div>
      </div>

      {/* Main Revenue & Net Profit Interactive Graph */}
      <div className="bg-surface-card border border-plum-border/80 rounded-2xl p-4 shadow-xs">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="p-1 rounded-md bg-primary/15 text-primary">
              <TrendingUp className="w-4 h-4" />
            </span>
            <h2 className="text-sm font-semibold text-plum-dark">
              Daily Revenue & Profit Trend
            </h2>
          </div>
          <span className="text-xs text-plum-muted">
            {currentRangeLabel}
          </span>
        </div>

        <AdminRevenueChart
          data={dashboardStats?.chartData || []}
          currency="Ks"
        />
      </div>

      {/* Operator Share & Top Packages Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Telecom Operator Breakdown */}
        <div className="bg-surface-card border border-plum-border/80 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-plum-border/60 pb-2">
            <div className="flex items-center gap-2">
              <span className="p-1 rounded-md bg-primary/15 text-primary">
                <Layers className="w-4 h-4" />
              </span>
              <h3 className="text-sm font-semibold text-plum-dark">Operator Breakdown</h3>
            </div>
            <span className="text-[11px] text-plum-muted">Revenue & Profit</span>
          </div>

          <div className="space-y-3 pt-1">
            {dashboardStats?.operatorStats?.map((op) => {
              const brand = OPERATOR_CONFIGS[op.operatorId];
              return (
                <div key={op.operatorId} className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <OperatorLogo id={op.operatorId} size="xs" />
                      <span className="font-semibold text-plum-dark">{op.name}</span>
                      <span className="text-[11px] text-plum-muted">
                        ({op.ordersCount} orders)
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="font-medium text-plum-dark">
                        {op.revenue.toLocaleString()} Ks
                      </span>
                      <span className="text-emerald-400 font-semibold ml-2">
                        +{op.profit.toLocaleString()}
                      </span>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="w-full bg-surface-page h-2 rounded-full overflow-hidden flex">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${Math.max(2, op.percentage)}%`,
                        backgroundColor: brand?.primaryColor || '#0EA5E9',
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Performing Packages */}
        <div className="bg-surface-card border border-plum-border/80 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between border-b border-plum-border/60 pb-2">
            <div className="flex items-center gap-2">
              <span className="p-1 rounded-md bg-status-green/15 text-status-green">
                <Package className="w-4 h-4" />
              </span>
              <h3 className="text-sm font-semibold text-plum-dark">Top Data Packages</h3>
            </div>
            {onNavigateTab && (
              <button
                type="button"
                onClick={() => onNavigateTab('admin-packages')}
                className="text-[11px] text-primary hover:text-primary-light flex items-center gap-0.5"
              >
                Manage <ArrowRight className="w-3 h-3" />
              </button>
            )}
          </div>

          <div className="space-y-2 pt-1">
            {(!dashboardStats?.topPackages || dashboardStats.topPackages.length === 0) ? (
              <div className="py-8 text-center text-xs text-plum-muted">
                No package orders completed yet in this period
              </div>
            ) : (
              dashboardStats.topPackages.map((pkg, idx) => (
                <div
                  key={pkg.id}
                  className="flex items-center justify-between p-2 rounded-xl bg-surface-page border border-plum-border/60 text-xs"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-5 h-5 rounded-full bg-surface-card border border-plum-border flex items-center justify-center font-bold text-[10px] text-plum-muted shrink-0">
                      {idx + 1}
                    </span>
                    <OperatorLogo id={pkg.operatorId} size="xs" />
                    <div className="truncate">
                      <p className="font-semibold text-plum-dark truncate">{pkg.name}</p>
                      <span className="text-[10px] text-plum-muted">{pkg.dataAllowance} • {pkg.ordersCount} sold</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-2">
                    <p className="font-medium text-plum-dark">{pkg.revenue.toLocaleString()} Ks</p>
                    <p className="text-[11px] font-semibold text-emerald-400">+{pkg.profit.toLocaleString()} Ks</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Customer Directory Quick Shortcut */}
        {onNavigateTab && (
          <div
            onClick={() => onNavigateTab('admin-customers')}
            className="p-3.5 bg-gradient-to-r from-surface-card to-slate-900 border border-plum-border hover:border-primary/60 rounded-2xl flex items-center justify-between cursor-pointer transition-all shadow-sm hover:shadow-md group"
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center text-primary shadow-xs">
                <Users className="w-4 h-4" />
              </div>
              <div>
                <h4 className="text-xs sm:text-sm font-bold text-slate-100 group-hover:text-primary transition-colors">
                  Customer Directory & Accounts
                </h4>
                <p className="text-[11px] text-slate-400">
                  Search registered users, inspect wallets & order histories
                </p>
              </div>
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
          </div>
        )}
      </div>
    </div>
  );
};
